import React from 'react';
import { CheckIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import { formatRwf } from '../../utils/currency';
import type { Billing, Plan } from '../../types/pricing';
type PlanCardsProps = {
  plans: Plan[];
  billing: Billing;
};
export function PlanCards({
  plans,
  billing
}: PlanCardsProps) {
  return <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-995" data-mp="36c7kjbtuz8os">
      {plans.map(plan => <li key={plan.id} className={plan.popular ? 'order-first md:order-none' : ''} data-id="element-996" data-mp="3u1ipz6m5lr99">
          <PlanCard plan={plan} billing={billing} data-id="element-997" data-mp="2tea37wdmzyu" />
        </li>)}
    </ul>;
}
function PlanCard({
  plan,
  billing
}: {
  plan: Plan;
  billing: Billing;
}) {
  const annual = billing === 'annual';
  const primary = plan.popular;
  return <article aria-labelledby={`plan-${plan.id}`} className={`relative flex h-full flex-col rounded-[14px] bg-white p-6 ${primary ? 'border-2 border-brand' : 'border border-canvas-line'}`} data-id="element-998" data-mp="3p7xov5p9kh5u">
      <div className="flex min-h-[28px] items-center justify-between gap-2" data-id="element-999" data-mp="39t3gd2c3rlhu">
        <h3 id={`plan-${plan.id}`} className="text-[20px] font-semibold tracking-[-0.02em] text-ink" data-id="element-1000" data-mp="39r0hdssz2i0z">
          {plan.name}
        </h3>
        {primary && <span className="rounded-full bg-brand px-2.5 py-1 text-[11px] font-semibold text-white" data-id="element-1001" data-mp="3jjy3sa134r4v">
            Most popular
          </span>}
      </div>
      <p className="mt-2 min-h-[42px] text-[14px] leading-[1.5] text-ink-muted" data-id="element-1002" data-mp="3sjbh5dsbxzuv">{plan.bestFor}</p>

      <div className="mt-6 min-h-[92px]" aria-live="polite" data-id="element-1003" data-mp="3nl9mdvljkc3k">
        <p className="text-[13px] font-semibold text-ink-muted" data-id="element-1004" data-mp="3nmugxl7uvv1f">RWF</p>
        <p className="mt-0.5 flex items-baseline gap-1.5 whitespace-nowrap" data-id="element-1005" data-mp="6bbnllq56xil">
          <span className="display text-[30px] font-semibold tabular-nums text-ink sm:text-[34px] xl:text-[30px] 2xl:text-[34px]" data-id="element-1006" data-mp="1da3cxgzcs5j">
            {(annual ? plan.annualTotal : plan.monthly).toLocaleString('en-US')}
          </span>
          <span className="text-[14px] text-ink-muted" data-id="element-1007" data-mp="236hgq1d18zos">{annual ? '/year' : '/month'}</span>
        </p>
        <p className="mt-1 whitespace-nowrap text-[13px] text-ink-muted" data-id="element-1008" data-mp="2sumyz6mk2u69">
          {annual ? `${formatRwf(plan.annualMonthly)}/month, billed yearly` : 'Billed monthly'}
        </p>
      </div>

      <SmartLink href={plan.cta.href} className={`mt-6 block rounded-[7px] px-5 py-3 text-center text-[15px] font-semibold transition-colors duration-150 ease-out ${primary ? 'bg-brand text-white hover:bg-brand-hover' : 'border border-ink/20 bg-white text-ink hover:border-ink/45'}`} data-id="element-1009" data-mp="31s5lygjrfiks">
        {plan.cta.label}
      </SmartLink>

      <div className="mt-7 border-t border-canvas-line pt-6" data-id="element-1010" data-mp="3kkrotkt349ev">
        <p className="min-h-[20px] text-[13px] font-semibold text-ink" data-id="element-1011" data-mp="3pjugm3k20j4w">{plan.featuresIntro ?? 'Includes:'}</p>
        <ul className="mt-4 space-y-3" data-id="element-1012" data-mp="3uj76kx6826bp">
          {plan.features.map(feature => <li key={feature} className="flex items-start gap-3 text-[14px] leading-[1.45] text-ink-soft" data-id="element-1013" data-mp="3bl7ziyd0u1p">
              <span className="mt-[2px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-brand-soft" data-id="element-1014" data-mp="1nuf7intux0fb">
                <CheckIcon className="h-3 w-3 text-brand" strokeWidth={2.5} aria-hidden="true" data-id="element-1015" data-mp="1ithgdlqvcw1o" />
              </span>
              {feature}
            </li>)}
        </ul>
      </div>
    </article>;
}